import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Heart,
  Link2,
  Loader2,
  MoreHorizontal,
  Pencil,
  Reply,
  Share2,
  Trash2,
} from "lucide-react";
import {
  clubAvatarInitial,
  formatClubCommentRelativeMeta,
  resolveClubMediaUrl,
} from "../utils/clubFeedPostUtils";
import { deleteClubPostComment, updateClubPostComment } from "../../../services/clubsApi";
import { deleteArtworkComment, updateArtworkComment } from "../../../services/artworkCommentsApi";
import ThreadComposer from "./ThreadComposer";
import {
  isMinimizedInStorage,
  persistClubThreadMinimized,
} from "../storage/threadMinimizedStorage";
import {
  INITIAL_VISIBLE_CHILDREN,
  INITIAL_VISIBLE_BRANCH_NODES,
  MAX_INLINE_THREAD_DEPTH,
  clubThreadCommentActionsRowClass,
  clubThreadCommentAvatarClass,
  clubThreadCommentBodyClass,
  clubThreadChildrenWrapClass,
  clubThreadChildBranchClass,
  clubThreadChildElbowClass,
  clubThreadChildRailHoverTargetClass,
  clubThreadChildRailClass,
  clubThreadInlineComposerWrapClass,
  clubThreadIndentClass,
  clubThreadCommentItemClass,
  clubThreadCommentMetaNameClass,
  clubThreadCommentMetaTimeClass,
  clubThreadContinueThreadButtonClass,
  clubThreadReplyButtonClass,
  clubThreadCommentActionButtonClass,
  clubThreadCommentActionCountClass,
  clubThreadCommentActionIconClass,
  clubThreadSharePopoverClass,
  clubThreadCommentOwnerMenuPortalClass,
  clubThreadSharePopoverItemClass,
  clubThreadSharePopoverWrapClass,
  clubThreadShowMoreButtonClass,
  clubThreadRailToggleButtonClass,
  clubThreadCommentCompactItemClass,
  clubThreadCommentCompactAvatarClass,
  clubThreadCommentInlineCollapseToggleClass,
  MAX_INDENT_DEPTH,
  MAX_PARENT_DEPTH_FOR_SIBLING_REPLY_PAGINATION,
} from "../presentation/threadPresentation.js";

function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

function countDescendants(nodes) {
  const list = Array.isArray(nodes) ? nodes : [];
  let total = 0;
  for (const n of list) {
    total += 1;
    total += countDescendants(n?.__children || []);
  }
  return total;
}

function parseClubCommentContent(rawContent) {
  if (rawContent != null && typeof rawContent === "object") {
    const attachments = Array.isArray(rawContent.attachments) ? rawContent.attachments : [];
    const text = rawContent.text != null ? String(rawContent.text) : "";
    return { text, attachments };
  }
  if (typeof rawContent !== "string") {
    return { text: String(rawContent ?? ""), attachments: [] };
  }

  const s = rawContent.trim();
  if (!s) return { text: "", attachments: [] };

  // Media-enabled comments are stored as JSON:
  // { text, attachments: [{ kind: 'gif'|'image', url }] }
  if (!s.startsWith("{")) return { text: rawContent, attachments: [] };

  try {
    const obj = JSON.parse(s);
    const attachments = Array.isArray(obj?.attachments) ? obj.attachments : [];
    const text = obj?.text != null ? String(obj.text) : "";
    return { text, attachments };
  } catch {
    return { text: rawContent, attachments: [] };
  }
}

const CLUB_INLINE_REPLY_MAX_LEN = 2000;

/** Single body line for soft-deleted comments (no mod vs author metadata from API). */
const REMOVED_COMMENT_MESSAGE = "This comment was removed.";

export default function CommentNode({
  comment,
  viewerId,
  canParticipate,
  depth = 0,
  children = [],
  activeReplyTargetId,
  setActiveReplyTargetId,
  inlineReplyText,
  setInlineReplyText,
  inlineSending,
  onSubmitInlineReply,
  onContinueThread,
  highlightedCommentId,
  slug,
  postId,
  /** "club" (forum + club thread shell, including `/thread` / `/community/thread`) vs "artwork" — routes edit/delete/share/minimize persistence. */
  commentSurface = "club",
  artworkId = null,
  onToggleCommentLike,
  onShareComment,
  forceExpandDirectChildren = false,
  forceExpandForIds,
  branchPreviewRemaining = INITIAL_VISIBLE_BRANCH_NODES,
  canModerateComments = false,
  onCommentMutated,
  onCommentActionError,
}) {
  const rawDepth = Math.max(0, Number(depth) || 0);
  const cAvatar = resolveClubMediaUrl(comment.profile_picture);
  const childList = Array.isArray(children) ? children : [];
  const [visibleChildCount, setVisibleChildCount] = useState(
    forceExpandDirectChildren ? childList.length : INITIAL_VISIBLE_CHILDREN
  );
  const [deepThreadExpanded, setDeepThreadExpanded] = useState(false);
  /** When true: hide body, actions, and entire subtree behind a compact summary row (Reddit-style). */
  const [threadMinimized, setThreadMinimized] = useState(false);
  const [branchPreviewExpanded, setBranchPreviewExpanded] = useState(false);
  const [branchHovered, setBranchHovered] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareHint, setShareHint] = useState("");
  const shareMenuRef = useRef(null);
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  /** Trigger wrap only — dropdown renders in a portal so it is not clipped or covered by media / nested thread stacking. */
  const ownerMenuTriggerWrapRef = useRef(null);
  const ownerMenuPanelRef = useRef(null);
  const [ownerMenuCoords, setOwnerMenuCoords] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const clampedDepth = Math.min(MAX_INDENT_DEPTH, rawDepth);
  const rowCommentId = Number(comment?.id ?? comment?.comment_id);
  const isActiveReply =
    Number.isFinite(rowCommentId) && Number(activeReplyTargetId) === rowCommentId;
  const canOpenReply = Boolean(viewerId) && Boolean(canParticipate);
  const hasChildren = childList.length > 0;
  const hiddenReplyCount = useMemo(() => countDescendants(childList), [childList]);
  const likeCount = Number(comment?.like_count) || 0;
  const likedByViewer = Boolean(comment?.liked_by_viewer);
  const isDeleted = Boolean(comment?.is_deleted);
  const isOwner =
    viewerId != null &&
    comment?.user_id != null &&
    Number(comment.user_id) === Number(viewerId);
  const showEdit = isOwner && !isDeleted && Boolean(canParticipate);
  const showDelete =
    !isDeleted && Boolean(canParticipate) && (isOwner || Boolean(canModerateComments));
  const showOwnerMenu = showEdit || showDelete;
  const postIdNum = useMemo(() => {
    const direct = Number(postId);
    if (Number.isFinite(direct)) return direct;
    if (typeof window === "undefined") return NaN;
    const p = String(window.location?.pathname || "");
    const club = p.match(/^\/clubs\/[^/]+\/posts\/([^/]+)/);
    if (club) {
      const fromUrl = Number(decodeURIComponent(club[1]));
      return Number.isFinite(fromUrl) ? fromUrl : NaN;
    }
    const neutral = p.match(/^\/(?:community\/)?thread\/([^/]+)/);
    if (neutral) {
      const fromUrl = Number(decodeURIComponent(neutral[1]));
      return Number.isFinite(fromUrl) ? fromUrl : NaN;
    }
    return NaN;
  }, [postId]);

  const artworkIdNum = useMemo(() => {
    const d = Number(artworkId);
    return Number.isFinite(d) ? d : NaN;
  }, [artworkId]);

  const debugClubComments =
    typeof process !== "undefined" && process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1";
  const { commentText, commentAttachments } = useMemo(() => {
    if (isDeleted) {
      return { commentText: "", commentAttachments: [] };
    }
    const parsed = parseClubCommentContent(comment?.content);
    const fromApi = (Array.isArray(comment?.attachments) ? comment.attachments : [])
      .map((a) => ({
        kind: a?.kind || "image",
        url: a?.url || a?.public_url || null,
      }))
      .filter((a) => a.url);
    const merged = [...parsed.attachments, ...fromApi];
    const seen = new Set();
    const att = [];
    for (const a of merged) {
      const u = String(a.url || "").trim();
      if (!u || seen.has(u)) continue;
      seen.add(u);
      att.push({ kind: a.kind, url: u });
    }
    if (commentSurface === "artwork") {
      return { commentText: parsed.text, commentAttachments: [] };
    }
    return { commentText: parsed.text, commentAttachments: att };
  }, [comment, commentSurface, isDeleted]);
  /** Top-level thread comments (depth 0) get a taller media cap; nested replies use Reddit-like smaller cap. */
  const commentMediaMaxHeightClass =
    rawDepth === 0 ? "max-h-[320px]" : "max-h-[240px]";
  const hasCommentText = Boolean(String(commentText || "").trim());
  /**
   * Reddit-style handoff: inline children only while the *next* reply would still be below the
   * max depth. Gating on (MAX - 1) ensures the continuation row replaces deeper descendants at
   * the boundary — we do not first render another nested card and then append "Continue thread"
   * under it (which felt like shrinking forever before the handoff).
   */
  const shouldGateDeepInline =
    rawDepth >= Math.max(0, MAX_INLINE_THREAD_DEPTH - 1);
  const shouldForceExpandByPath =
    Number.isFinite(rowCommentId) && forceExpandForIds?.has?.(rowCommentId);
  const branchBudgetRemaining = Math.max(0, Number(branchPreviewRemaining) || 0);
  const displayUsername = isDeleted ? "[deleted]" : comment.username || "Member";
  const commentTimeMeta = useMemo(
    () => formatClubCommentRelativeMeta(comment?.created_at),
    [comment?.created_at]
  );

  const syncStoredMinimized = useCallback(
    (minimized) => {
      if (commentSurface !== "club") return;
      const pid = postIdNum;
      const cid = Number(comment?.id);
      if (!Number.isFinite(pid) || !Number.isFinite(cid)) return;
      persistClubThreadMinimized({ postId: pid, commentId: cid, minimized });
    },
    [commentSurface, postIdNum, comment?.id]
  );

  const userMinimizeThread = useCallback(() => {
    setThreadMinimized(true);
    syncStoredMinimized(true);
  }, [syncStoredMinimized]);

  const userExpandThread = useCallback(() => {
    setThreadMinimized(false);
    syncStoredMinimized(false);
  }, [syncStoredMinimized]);

  /** Hydrate from localStorage; forced path expansion always wins over stored minimize. */
  useLayoutEffect(() => {
    if (commentSurface !== "club") {
      setThreadMinimized(false);
      return;
    }
    const pid = postIdNum;
    const cid = Number(comment?.id);
    if (!Number.isFinite(pid) || !Number.isFinite(cid)) return;
    if (shouldForceExpandByPath) {
      setThreadMinimized(false);
      return;
    }
    if (isMinimizedInStorage(pid, cid)) {
      setThreadMinimized(true);
    }
  }, [commentSurface, postIdNum, comment?.id, shouldForceExpandByPath]);

  useEffect(() => {
    if (!forceExpandDirectChildren) return;
    setVisibleChildCount((prev) => Math.max(prev, childList.length));
  }, [forceExpandDirectChildren, childList.length]);

  useEffect(() => {
    if (!shouldForceExpandByPath) return;
    setVisibleChildCount((prev) => Math.max(prev, childList.length));
    if (shouldGateDeepInline) setDeepThreadExpanded(true);
    setThreadMinimized(false);
    setBranchPreviewExpanded(true);
  }, [shouldForceExpandByPath, childList.length, shouldGateDeepInline]);

  useEffect(() => {
    if (!isActiveReply) return;
    setThreadMinimized(false);
  }, [isActiveReply]);

  useEffect(() => {
    if (!shareOpen) return;
    const onDoc = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShareOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShareOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [shareOpen]);

  useEffect(() => {
    if (!ownerMenuOpen) return;
    const onDoc = (e) => {
      if (ownerMenuTriggerWrapRef.current?.contains(e.target)) return;
      if (ownerMenuPanelRef.current?.contains(e.target)) return;
      setOwnerMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOwnerMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [ownerMenuOpen]);

  useLayoutEffect(() => {
    if (!ownerMenuOpen || !showOwnerMenu || editing) {
      setOwnerMenuCoords(null);
      return undefined;
    }
    const el = ownerMenuTriggerWrapRef.current;
    if (!el) {
      setOwnerMenuCoords(null);
      return undefined;
    }
    const updatePosition = () => {
      const r = el.getBoundingClientRect();
      const minW = 176;
      const pad = 8;
      let left = r.left;
      const vw = window.innerWidth;
      if (left + minW > vw - pad) {
        left = Math.max(pad, vw - minW - pad);
      }
      setOwnerMenuCoords({
        top: r.bottom + 8,
        left,
        minWidth: Math.max(minW, r.width),
      });
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [ownerMenuOpen, showOwnerMenu, editing]);

  const hasContinuationHandler = typeof onContinueThread === "function";
  /**
   * When Clubs/ArtHouse pass onContinueThread, never use deepThreadExpanded to force inline
   * rendering past the gate — that produced grey "+N more" on the parent and green continuation
   * on children at the same time. Continuation is the only way past MAX_INLINE_THREAD_DEPTH.
   */
  const canRenderBranchPreview =
    !hasChildren ? false : branchBudgetRemaining > 0 || branchPreviewExpanded || shouldForceExpandByPath;
  const canRenderChildrenInline =
    !hasChildren
      ? false
      : canRenderBranchPreview &&
        (!shouldGateDeepInline || (!hasContinuationHandler && deepThreadExpanded));
  const hiddenByBranchPreview = hasChildren && !canRenderBranchPreview ? hiddenReplyCount : 0;

  const allowSiblingShowMore = rawDepth < MAX_PARENT_DEPTH_FOR_SIBLING_REPLY_PAGINATION;
  const cappedVisibleChildCount = Math.max(
    INITIAL_VISIBLE_CHILDREN,
    Math.min(visibleChildCount, childList.length)
  );
  const sliceEnd = allowSiblingShowMore ? cappedVisibleChildCount : childList.length;
  const visibleChildren = childList.slice(0, sliceEnd);
  const remainingDirectChildren = allowSiblingShowMore
    ? Math.max(0, childList.length - sliceEnd)
    : 0;

  const buildCommentPath = () => {
    if (commentSurface === "artwork") {
      if (!Number.isFinite(artworkIdNum) || !Number.isFinite(rowCommentId)) return null;
      return `/arthouse/art/${encodeURIComponent(String(artworkIdNum))}?comment=${encodeURIComponent(
        String(rowCommentId)
      )}`;
    }
    const fallbackPath = String(window?.location?.pathname || "");
    const clubMatch = fallbackPath.match(/^\/clubs\/([^/]+)\/posts\/([^/]+)/);
    const routeSlug = String(slug || "").trim() || (clubMatch ? decodeURIComponent(clubMatch[1]) : "");
    const routePostId =
      postId != null && String(postId).trim() !== ""
        ? String(postId)
        : clubMatch
          ? decodeURIComponent(clubMatch[2])
          : "";
    const neutralMatch = fallbackPath.match(/^\/(?:community\/)?thread\/([^/]+)/);
    const neutralPostId =
      (routePostId && String(routePostId).trim()) ||
      (neutralMatch ? decodeURIComponent(neutralMatch[1]) : "");

    if (!comment?.id || !neutralPostId) return null;

    if (routeSlug) {
      return `/clubs/${encodeURIComponent(routeSlug)}/posts/${encodeURIComponent(
        neutralPostId
      )}/comments/${encodeURIComponent(String(comment.id))}/thread`;
    }

    if (fallbackPath.startsWith("/community/thread/")) {
      return `/community/thread/${encodeURIComponent(neutralPostId)}?comment=${encodeURIComponent(
        String(comment.id)
      )}`;
    }
    return `/thread/${encodeURIComponent(neutralPostId)}?comment=${encodeURIComponent(String(comment.id))}`;
  };

  /** Permalink on the main post page (scroll + highlight via ?comment=). */
  const buildFullPostCommentPath = () => {
    if (commentSurface === "artwork") {
      if (!Number.isFinite(artworkIdNum)) return null;
      return `/arthouse/art/${encodeURIComponent(String(artworkIdNum))}`;
    }
    const fallbackPath = String(window?.location?.pathname || "");
    const clubMatch = fallbackPath.match(/^\/clubs\/([^/]+)\/posts\/([^/]+)/);
    const routeSlug = String(slug || "").trim() || (clubMatch ? decodeURIComponent(clubMatch[1]) : "");
    const routePostId =
      postId != null && String(postId).trim() !== ""
        ? String(postId)
        : clubMatch
          ? decodeURIComponent(clubMatch[2])
          : "";
    const neutralMatch = fallbackPath.match(/^\/(?:community\/)?thread\/([^/]+)/);
    const neutralPostId =
      (routePostId && String(routePostId).trim()) ||
      (neutralMatch ? decodeURIComponent(neutralMatch[1]) : "");

    if (!comment?.id || !neutralPostId) return null;

    if (routeSlug) {
      return `/clubs/${encodeURIComponent(routeSlug)}/posts/${encodeURIComponent(
        neutralPostId
      )}?comment=${encodeURIComponent(String(comment.id))}`;
    }
    if (fallbackPath.startsWith("/community/thread/")) {
      return `/community/thread/${encodeURIComponent(neutralPostId)}?comment=${encodeURIComponent(
        String(comment.id)
      )}`;
    }
    return `/thread/${encodeURIComponent(neutralPostId)}?comment=${encodeURIComponent(String(comment.id))}`;
  };

  const onLikeClick = async () => {
    if (
      isDeleted ||
      likeBusy ||
      !viewerId ||
      !canParticipate ||
      typeof onToggleCommentLike !== "function"
    )
      return;
    try {
      setLikeBusy(true);
      await onToggleCommentLike(comment);
    } finally {
      setLikeBusy(false);
    }
  };

  const onShareClick = async () => {
    const path = buildCommentPath();
    if (!path) return;
    const absolute = `${window.location.origin}${path}`;
    if (typeof onShareComment === "function") {
      await onShareComment({ comment, path, url: absolute });
      return;
    }
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
      setShareHint("Link copied");
      window.setTimeout(() => setShareHint(""), 1300);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      prompt("Copy this link:", absolute);
    }
  };

  const onShareMenuSelect = async (key) => {
    if (key === "copy") {
      await onShareClick();
      setShareOpen(false);
      return;
    }
    if (key === "copyFullPost") {
      const path = buildFullPostCommentPath();
      if (!path) return;
      const absolute = `${window.location.origin}${path}`;
      if (typeof onShareComment === "function") {
        await onShareComment({ comment, path, url: absolute, variant: "fullPost" });
        setShareOpen(false);
        return;
      }
      try {
        await navigator.clipboard.writeText(absolute);
        setCopied(true);
        setShareHint("Discussion link copied");
        window.setTimeout(() => setShareHint(""), 1300);
        window.setTimeout(() => setCopied(false), 1400);
      } catch {
        prompt("Copy this link:", absolute);
      }
      setShareOpen(false);
      return;
    }
  };

  const beginEdit = () => {
    setOwnerMenuOpen(false);
    setThreadMinimized(false);
    setEditText(String(commentText || ""));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditText("");
  };

  const saveEdit = async () => {
    if (commentSurface === "artwork") {
      if (!Number.isFinite(rowCommentId)) {
        const msg = "Missing comment id. Refresh the page and try again.";
        onCommentActionError?.(msg);
        return;
      }
      const t = String(editText || "").trim();
      if (!t) {
        onCommentActionError?.("Comment cannot be empty.");
        return;
      }
      try {
        setEditSaving(true);
        onCommentActionError?.("");
        const raw = await updateArtworkComment(rowCommentId, String(editText ?? ""));
        onCommentMutated?.(raw);
        setEditing(false);
        setEditText("");
      } catch (e) {
        onCommentActionError?.(e?.message || "Could not save edit.");
      } finally {
        setEditSaving(false);
      }
      return;
    }
    if (!Number.isFinite(postIdNum) || comment?.id == null) {
      const msg = "Missing post or comment id. Refresh the page and try again.";
      onCommentActionError?.(msg);
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.warn("[ClubCommentNode saveEdit] blocked", { postId, postIdNum, commentId: comment?.id });
      }
      return;
    }
    const hasAtt = commentAttachments.length > 0;
    const t = String(editText || "").trim();
    if (!t && !hasAtt) {
      onCommentActionError?.("Comment cannot be empty.");
      return;
    }
    try {
      setEditSaving(true);
      onCommentActionError?.("");
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubCommentNode saveEdit] PATCH", {
          postId: postIdNum,
          commentId: comment.id,
          textLen: String(editText || "").length,
        });
      }
      const raw = await updateClubPostComment(
        postIdNum,
        comment.id,
        String(editText ?? "")
      );
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubCommentNode saveEdit] response", raw);
      }
      if (typeof onCommentMutated === "function") {
        onCommentMutated(raw);
      } else if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.warn("[ClubCommentNode saveEdit] onCommentMutated is not a function; UI will not update.");
      }
      setEditing(false);
      setEditText("");
    } catch (e) {
      onCommentActionError?.(e?.message || "Could not save edit.");
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.error("[ClubCommentNode saveEdit] error", e);
      }
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (commentSurface === "artwork") {
      if (!Number.isFinite(rowCommentId)) {
        onCommentActionError?.("Missing comment id. Refresh the page and try again.");
        return;
      }
      if (
        !window.confirm(
          "Delete this comment? It becomes a placeholder and replies stay here."
        )
      )
        return;
      setOwnerMenuOpen(false);
      try {
        setDeleteBusy(true);
        onCommentActionError?.("");
        const raw = await deleteArtworkComment(rowCommentId);
        onCommentMutated?.(raw);
      } catch (e) {
        onCommentActionError?.(e?.message || "Could not delete comment.");
      } finally {
        setDeleteBusy(false);
      }
      return;
    }
    if (!Number.isFinite(postIdNum) || comment?.id == null) {
      const msg = "Missing post or comment id. Refresh the page and try again.";
      onCommentActionError?.(msg);
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.warn("[ClubCommentNode confirmDelete] blocked", {
          postId,
          postIdNum,
          commentId: comment?.id,
        });
      }
      return;
    }
    if (
      !window.confirm(
        "Delete this comment? It becomes a placeholder and replies stay here."
      )
    )
      return;
    setOwnerMenuOpen(false);
    try {
      setDeleteBusy(true);
      onCommentActionError?.("");
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubCommentNode confirmDelete] DELETE", {
          postId: postIdNum,
          commentId: comment.id,
        });
      }
      const raw = await deleteClubPostComment(postIdNum, comment.id);
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubCommentNode confirmDelete] response", raw);
      }
      if (typeof onCommentMutated === "function") {
        onCommentMutated(raw);
      } else if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.warn(
          "[ClubCommentNode confirmDelete] onCommentMutated is not a function; UI will not update."
        );
      }
    } catch (e) {
      onCommentActionError?.(e?.message || "Could not delete comment.");
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.error("[ClubCommentNode confirmDelete] error", e);
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const canSaveEdit =
    commentSurface === "artwork"
      ? Boolean(String(editText || "").trim())
      : Boolean(String(editText || "").trim()) || commentAttachments.length > 0;

  /** Reply branch is a later DOM sibling; without a higher z-index it paints over dropdowns / Save row. */
  const elevateCommentCard = ownerMenuOpen || shareOpen || editing;

  return (
    <div
      data-comment-id={
        Number.isFinite(rowCommentId) ? String(rowCommentId) : undefined
      }
      className={[
        "relative min-w-0 max-w-full",
        clubThreadIndentClass(clampedDepth),
      ].join(" ")}
    >
      <div
        className={cn(
          "relative min-w-0 max-w-full",
          Number.isFinite(rowCommentId) &&
            Number(highlightedCommentId) === rowCommentId
            ? "ring-1 ring-emerald-300/45 bg-emerald-500/[0.06] rounded-xl transition-colors duration-300"
            : "",
          elevateCommentCard ? "z-[35]" : "z-0"
        )}
      >
        {threadMinimized && !editing ? (
          <div className="relative min-w-0 max-w-full">
            <div className={clubThreadCommentCompactItemClass}>
              <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                <button
                  type="button"
                  className={clubThreadCommentInlineCollapseToggleClass}
                  aria-expanded={false}
                  aria-label={
                    hasChildren
                      ? isDeleted
                        ? `Expand removed comment and ${hiddenReplyCount} ${
                            hiddenReplyCount === 1 ? "reply" : "replies"
                          }`
                        : `Expand comment and ${hiddenReplyCount} ${
                            hiddenReplyCount === 1 ? "reply" : "replies"
                          }`
                      : isDeleted
                        ? "Expand removed comment"
                        : "Expand comment"
                  }
                  onClick={userExpandThread}
                >
                  +
                </button>
                {!isDeleted ? (
                  <div
                    className={cn(
                      clubThreadCommentCompactAvatarClass,
                      "overflow-hidden"
                    )}
                  >
                    {cAvatar ? (
                      <img src={cAvatar} alt="" className="h-full w-full object-cover" />
                    ) : (
                      clubAvatarInitial(comment.username)
                    )}
                  </div>
                ) : null}
                <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] sm:text-xs">
                  <span
                    className={cn(
                      clubThreadCommentMetaNameClass,
                      "text-xs font-semibold truncate max-w-[min(100%,14rem)]",
                      isDeleted ? "italic text-white/45" : "text-white"
                    )}
                  >
                    {displayUsername}
                  </span>
                  <span className="text-white/30 shrink-0" aria-hidden>
                    ·
                  </span>
                  <span className="inline-flex items-center gap-0.5 tabular-nums text-gray-400 shrink-0">
                    <Heart
                      className={cn(
                        "h-3 w-3 shrink-0",
                        likedByViewer ? "fill-current text-emerald-300/90" : "text-white/40"
                      )}
                    />
                    {likeCount}
                  </span>
                  {hasChildren ? (
                    <>
                      <span className="text-white/30 shrink-0" aria-hidden>
                        ·
                      </span>
                      <span className="text-gray-400 shrink-0">
                        {hiddenReplyCount}{" "}
                        {hiddenReplyCount === 1 ? "reply" : "replies"}
                      </span>
                    </>
                  ) : null}
                  <span className="text-white/30 shrink-0" aria-hidden>
                    ·
                  </span>
                  <time
                    dateTime={commentTimeMeta.dateTimeAttr || undefined}
                    className="text-gray-500 tabular-nums shrink-0"
                    title={commentTimeMeta.title || undefined}
                  >
                    {commentTimeMeta.label || commentTimeMeta.title || "—"}
                  </time>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {hasChildren && !editing ? (
              <button
                type="button"
                aria-label="Collapse comment, replies, and actions"
                aria-expanded={true}
                onClick={userMinimizeThread}
                className={clubThreadRailToggleButtonClass}
              >
                −
              </button>
            ) : null}

            <div className="relative min-w-0 max-w-full">
              <div className={clubThreadCommentItemClass}>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex items-start gap-2 shrink-0">
                    {!hasChildren && !isDeleted && !editing ? (
                      <button
                        type="button"
                        className={cn(clubThreadCommentInlineCollapseToggleClass, "mt-0.5")}
                        aria-label="Collapse comment"
                        aria-expanded
                        onClick={userMinimizeThread}
                      >
                        −
                      </button>
                    ) : null}
                    <div
                      className={cn(
                        clubThreadCommentAvatarClass,
                        isDeleted ? "opacity-50 grayscale-[0.35]" : ""
                      )}
                    >
                      {cAvatar && !isDeleted ? (
                        <img src={cAvatar} alt="" className="h-full w-full object-cover" />
                      ) : (
                        clubAvatarInitial(isDeleted ? "?" : comment.username)
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <p
                        className={cn(
                          clubThreadCommentMetaNameClass,
                          isDeleted ? "italic text-white/45" : ""
                        )}
                      >
                        {displayUsername}
                      </p>
                      <time
                        dateTime={commentTimeMeta.dateTimeAttr || undefined}
                        className={clubThreadCommentMetaTimeClass}
                        title={commentTimeMeta.title || undefined}
                      >
                        {commentTimeMeta.label || commentTimeMeta.title || "—"}
                      </time>
                      {!isDeleted && comment?.edited_at ? (
                        <span className="text-[11px] text-white/35">(edited)</span>
                      ) : null}
                    </div>
                    {editing ? (
                <div className="mt-2 space-y-2">
                  {commentAttachments.length ? (
                    <p className="text-[11px] text-white/45 leading-relaxed">
                      Text only — media stays as posted.
                    </p>
                  ) : null}
                  <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden focus-within:border-white/20 transition-colors">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full min-h-[100px] sm:min-h-[120px] resize-none bg-transparent px-3 sm:px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 outline-none border-0 ring-0 focus:ring-0"
                      maxLength={CLUB_INLINE_REPLY_MAX_LEN}
                      disabled={editSaving}
                      aria-describedby={`club-edit-comment-len-${comment?.id ?? "x"}`}
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={editSaving || !canSaveEdit}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/35 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-45 disabled:cursor-not-allowed"
                    >
                      {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={editSaving}
                      className="inline-flex items-center rounded-lg border border-white/10 bg-transparent px-3 py-1.5 text-xs text-gray-300 hover:bg-white/[0.05] disabled:opacity-45"
                    >
                      Cancel
                    </button>
                    </div>
                    <p
                      id={`club-edit-comment-len-${comment?.id ?? "x"}`}
                      className="text-[10px] text-gray-500 tabular-nums shrink-0"
                      aria-live="polite"
                    >
                      {String(editText || "").length}/{CLUB_INLINE_REPLY_MAX_LEN}
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className={cn(
                    clubThreadCommentBodyClass,
                    isDeleted ? "text-white/40 italic" : ""
                  )}
                >
                  {isDeleted ? (
                    <p
                      className="text-sm leading-relaxed text-white/45 italic"
                      role="status"
                    >
                      {REMOVED_COMMENT_MESSAGE}
                    </p>
                  ) : commentText ? (
                    <p className="whitespace-pre-wrap break-words leading-relaxed">{commentText}</p>
                  ) : null}

                  {!isDeleted && commentAttachments.length ? (
                    <div
                      className={cn(
                        "w-full min-w-0 max-w-full space-y-2.5",
                        hasCommentText ? "mt-3" : "mt-2"
                      )}
                    >
                      {commentAttachments.map((att, idx) => {
                        const kind = String(att?.kind || "").toLowerCase();
                        const url = resolveClubMediaUrl(att?.url || att?.public_url);
                        if (!url) return null;
                        return (
                          <div
                            key={att?.url || `${kind}_${idx}`}
                            className="w-full min-w-0 max-w-full rounded-xl border border-white/[0.10] bg-[#050810] p-1.5 sm:p-2 ring-1 ring-inset ring-white/[0.04]"
                          >
                            <img
                              src={url}
                              alt={kind === "gif" ? "GIF" : "Image"}
                              className={cn(
                                "block w-full max-w-full object-contain rounded-lg",
                                commentMediaMaxHeightClass
                              )}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              )}
              <div className={clubThreadCommentActionsRowClass}>
              <button
                type="button"
                onClick={onLikeClick}
                disabled={
                  isDeleted || likeBusy || !viewerId || !canParticipate
                }
                aria-label={
                  isDeleted
                    ? "Likes are not available on removed comments"
                    : likedByViewer
                      ? `Unlike, ${likeCount} likes`
                      : `Like, ${likeCount} likes`
                }
                className={clubThreadCommentActionButtonClass({
                  active: likedByViewer,
                  disabled:
                    isDeleted || likeBusy || !viewerId || !canParticipate,
                })}
              >
                <Heart
                  className={[
                    clubThreadCommentActionIconClass,
                    likedByViewer ? "fill-current text-emerald-300" : "text-white/70",
                  ].join(" ")}
                />
                <span className={clubThreadCommentActionCountClass}>{likeCount}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!canOpenReply || editing) return;
                  const nextActive = isActiveReply
                    ? null
                    : !Number.isFinite(rowCommentId)
                      ? null
                      : rowCommentId;
                  setActiveReplyTargetId?.(nextActive);
                  if (!isActiveReply) setInlineReplyText?.("");
                }}
                disabled={!canOpenReply || editing}
                title={
                  isDeleted && canOpenReply && !editing
                    ? "Reply stays with these replies under this removed comment"
                    : undefined
                }
                aria-label={
                  isDeleted && canOpenReply && !editing
                    ? "Reply under removed comment"
                    : "Reply"
                }
                className={clubThreadReplyButtonClass({
                  canOpenReply: canOpenReply && !editing,
                  isActiveReply,
                })}
              >
                <Reply className={clubThreadCommentActionIconClass} />
                Reply
              </button>
              <div className={clubThreadSharePopoverWrapClass} ref={shareMenuRef}>
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className={clubThreadCommentActionButtonClass({ active: copied || shareOpen })}
                  aria-label={
                    isDeleted
                      ? copied
                        ? "Link copied"
                        : "Share link to these replies (comment removed)"
                      : copied
                        ? "Link copied"
                        : "Share"
                  }
                  title={isDeleted ? "Permalink to these replies (comment removed)" : undefined}
                >
                  <Share2 className={clubThreadCommentActionIconClass} />
                  {copied ? "Copied" : "Share"}
                </button>
                {shareOpen ? (
                  <div className={clubThreadSharePopoverClass} role="menu" aria-label="Share options">
                    <button
                      type="button"
                      className={clubThreadSharePopoverItemClass}
                      onClick={() => onShareMenuSelect("copy")}
                    >
                      <Link2 className={clubThreadCommentActionIconClass} />
                      Copy replies link
                    </button>
                    <button
                      type="button"
                      className={clubThreadSharePopoverItemClass}
                      onClick={() => onShareMenuSelect("copyFullPost")}
                    >
                      <Link2 className={clubThreadCommentActionIconClass} />
                      {commentSurface === "artwork" ? "Copy artwork page link" : "Copy link on post page"}
                    </button>
                  </div>
                ) : null}
              </div>
              {showOwnerMenu && !editing ? (
                <div className={clubThreadSharePopoverWrapClass} ref={ownerMenuTriggerWrapRef}>
                  <button
                    type="button"
                    onClick={() => setOwnerMenuOpen((v) => !v)}
                    disabled={deleteBusy}
                    className={clubThreadCommentActionButtonClass({
                      active: ownerMenuOpen,
                      disabled: deleteBusy,
                    })}
                    aria-label="Comment options"
                    aria-haspopup="menu"
                    aria-expanded={ownerMenuOpen}
                  >
                    {deleteBusy ? (
                      <Loader2 className={cn(clubThreadCommentActionIconClass, "animate-spin")} />
                    ) : (
                      <MoreHorizontal className={clubThreadCommentActionIconClass} />
                    )}
                  </button>
                </div>
              ) : null}
              </div>
              {isActiveReply ? (
                <p className="mt-1 text-[11px] sm:text-xs text-emerald-200/85">
                  Replying to {displayUsername}
                </p>
              ) : null}
              {shareHint ? <p className="mt-1 text-[11px] sm:text-xs text-emerald-200/80">{shareHint}</p> : null}
            </div>
          </div>
        </div>

        {isActiveReply ? (
          <div className={clubThreadInlineComposerWrapClass}>
            <ThreadComposer
              viewerId={viewerId}
              canParticipate={canParticipate}
              replyText={inlineReplyText || ""}
              sending={inlineSending}
              onReplyTextChange={(v) => setInlineReplyText?.(v)}
              showCancelButton
              onCancel={() => {
                setActiveReplyTargetId?.(null);
                setInlineReplyText?.("");
              }}
              allowAttachments={commentSurface !== "artwork"}
              onSubmit={(composerPayload) =>
                onSubmitInlineReply?.({
                  ...composerPayload,
                  comment,
                  depth: rawDepth,
                  shouldGateDeepInline,
                })
              }
              className="px-0 py-0 border-0"
            />
          </div>
        ) : null}
            </div>
          </>
        )}
      </div>

      {childList.length > 0 && !threadMinimized ? (
        <div className={cn(clubThreadChildrenWrapClass, "relative z-0")}>
          <div className={clubThreadChildBranchClass}>
            <div
              className={clubThreadChildRailHoverTargetClass}
              onMouseEnter={() => setBranchHovered(true)}
              onMouseLeave={() => setBranchHovered(false)}
              aria-hidden
            />
            <span aria-hidden className={clubThreadChildRailClass(branchHovered)} />
            {canRenderChildrenInline ? (
              <>
                {visibleChildren.map((child) => (
                  <div key={child.id} className="relative min-w-0 max-w-full">
                    <span aria-hidden className={clubThreadChildElbowClass(branchHovered)} />
                    <CommentNode
                      comment={child}
                      viewerId={viewerId}
                      canParticipate={canParticipate}
                      depth={rawDepth + 1}
                      children={child.__children || []}
                      activeReplyTargetId={activeReplyTargetId}
                      setActiveReplyTargetId={setActiveReplyTargetId}
                      inlineReplyText={inlineReplyText}
                      setInlineReplyText={setInlineReplyText}
                      inlineSending={inlineSending}
                      onSubmitInlineReply={onSubmitInlineReply}
                      onContinueThread={onContinueThread}
                      highlightedCommentId={highlightedCommentId}
                      slug={slug}
                      postId={postId}
                      commentSurface={commentSurface}
                      artworkId={artworkId}
                      onToggleCommentLike={onToggleCommentLike}
                      onShareComment={onShareComment}
                      forceExpandDirectChildren={false}
                      forceExpandForIds={forceExpandForIds}
                      branchPreviewRemaining={branchPreviewExpanded ? INITIAL_VISIBLE_BRANCH_NODES : branchBudgetRemaining - 1}
                      canModerateComments={canModerateComments}
                      onCommentMutated={onCommentMutated}
                      onCommentActionError={onCommentActionError}
                    />
                  </div>
                ))}
                {remainingDirectChildren > 0 ? (
                  <button
                    type="button"
                    onClick={() => setVisibleChildCount(childList.length)}
                    className={clubThreadShowMoreButtonClass}
                  >
                    + {remainingDirectChildren} more{" "}
                    {remainingDirectChildren === 1 ? "reply" : "replies"}
                  </button>
                ) : null}
              </>
            ) : hiddenByBranchPreview > 0 ? (
              <button
                type="button"
                onClick={() => setBranchPreviewExpanded(true)}
                className={clubThreadShowMoreButtonClass}
                aria-label={`Show ${hiddenByBranchPreview} ${
                  hiddenByBranchPreview === 1 ? "reply" : "replies"
                }`}
              >
                Show {hiddenByBranchPreview}{" "}
                {hiddenByBranchPreview === 1 ? "reply" : "replies"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (typeof onContinueThread === "function") {
                    if (!Number.isFinite(rowCommentId)) return;
                    const firstChild = childList[0];
                    const fid = Number(firstChild?.id ?? firstChild?.comment_id);
                    const focusCommentId = Number.isFinite(fid) ? fid : undefined;
                    onContinueThread(rowCommentId, { focusCommentId });
                    return;
                  }
                  setDeepThreadExpanded(true);
                }}
                className={clubThreadContinueThreadButtonClass}
                aria-label={
                  hiddenReplyCount > 0
                    ? `View more replies (${hiddenReplyCount} ${
                        hiddenReplyCount === 1 ? "reply" : "replies"
                      })`
                    : "View more replies"
                }
              >
                {hiddenReplyCount > 0
                  ? `View more replies · ${hiddenReplyCount} ${
                      hiddenReplyCount === 1 ? "reply" : "replies"
                    } →`
                  : "View more replies →"}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {ownerMenuOpen &&
      showOwnerMenu &&
      !editing &&
      ownerMenuCoords &&
      typeof document !== "undefined"
        ? createPortal(
            <div
              ref={ownerMenuPanelRef}
              role="menu"
              aria-label="Comment options"
              className={clubThreadCommentOwnerMenuPortalClass}
              style={{
                top: ownerMenuCoords.top,
                left: ownerMenuCoords.left,
                minWidth: ownerMenuCoords.minWidth,
              }}
            >
              {showEdit ? (
                <button
                  type="button"
                  role="menuitem"
                  className={clubThreadSharePopoverItemClass}
                  onClick={beginEdit}
                >
                  <Pencil className={clubThreadCommentActionIconClass} />
                  Edit
                </button>
              ) : null}
              {showDelete ? (
                <button
                  type="button"
                  role="menuitem"
                  className={clubThreadSharePopoverItemClass}
                  onClick={() => void confirmDelete()}
                  disabled={deleteBusy}
                >
                  <Trash2 className={clubThreadCommentActionIconClass} />
                  Delete
                </button>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
